import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { GoogleMap, InfoWindow } from '@react-google-maps/api';
import { Loader } from '@googlemaps/js-api-loader';
import { UrlApi } from '../../../services/apirest';
import Navbar from '../../Navbar';
import { logEventos } from '../../../services/services';

const ENDPOINT_BASE = '/mapaventasenvasadonuevo';

// ============================================================
// Configuración
// ============================================================
const GOOGLE_MAPS_KEY = process.env.REACT_APP_Google_maps_key;

// ------------------------------------------------------------
// Carga de Google Maps participando del mismo mecanismo interno
// (`@googlemaps/js-api-loader`) que usan los ~15 componentes viejos
// de la app (GeoZonas, ClientesRuta, MapaCliEnv, etc. — todos con
// <LoadScript>). Motivo del cambio respecto a inyectar un <script>
// a mano: esos componentes esperan que la carga pase por ESE
// mecanismo puntual — un <script> nuestro por fuera de ese sistema
// deja la ventana con window.google.maps disponible, pero la PROMESA
// interna de ELLOS nunca se resuelve, y quedan colgados en loading.
//
//   1. Si ya está cargado (window.google.maps existe) → listo.
//   2. Si no, intentamos Loader.load() — si ningún otro componente
//      reclamó ya el singleton con opciones distintas, esto carga
//      Maps correctamente y queda compatible con el resto de la app.
//   3. Si otro componente YA reclamó el singleton con opciones
//      distintas a las nuestras, Loader.load() rechaza la promesa —
//      ahí caemos a esperar por window.google.maps con polling, sin
//      inyectar nada nuestro (para no duplicar el choque).
// ------------------------------------------------------------
function useGoogleMapsManual(apiKey) {
    const [cargado, setCargado] = useState(() => !!(window.google && window.google.maps));

    useEffect(() => {
        if (window.google && window.google.maps) {
            setCargado(true);
            return;
        }

        let cancelado = false;
        let intervalo = null;

        const loader = new Loader({
            apiKey,
            version: 'weekly',
            libraries: ['places'],
        });

        loader.load()
            .then(() => {
                if (!cancelado) setCargado(true);
            })
            .catch(() => {
                // El singleton ya fue reclamado por otro componente con
                // opciones distintas. Esperamos a que termine de cargar
                // por el mecanismo que sea, sin inyectar nada nuestro.
                intervalo = setInterval(() => {
                    if (window.google && window.google.maps) {
                        clearInterval(intervalo);
                        if (!cancelado) setCargado(true);
                    }
                }, 150);
            });

        return () => {
            cancelado = true;
            if (intervalo) clearInterval(intervalo);
        };
    }, [apiKey]);

    return cargado;
}

// Alto reservado para el navbar y el footer fijos del layout, para que
// el mapa no quede tapado detrás de ninguno de los dos. Ajustar estos
// dos valores si el navbar/footer real mide distinto.
const ALTO_NAVBAR = 55;
const ALTO_FOOTER = 20;

const MAP_CONTAINER_STYLE = { width: '100%', height: `calc(100vh - ${ALTO_NAVBAR + ALTO_FOOTER}px)` };
const CENTRO_DEFAULT = { lat: -34.6037, lng: -58.3816 }; // ajustar al centro real de tu zona de operación

const MESES = [
    { valor: 1, nombre: 'Enero' }, { valor: 2, nombre: 'Febrero' }, { valor: 3, nombre: 'Marzo' },
    { valor: 4, nombre: 'Abril' }, { valor: 5, nombre: 'Mayo' }, { valor: 6, nombre: 'Junio' },
    { valor: 7, nombre: 'Julio' }, { valor: 8, nombre: 'Agosto' }, { valor: 9, nombre: 'Septiembre' },
    { valor: 10, nombre: 'Octubre' }, { valor: 11, nombre: 'Noviembre' }, { valor: 12, nombre: 'Diciembre' }
];

// Rangos de kilos para color/tamaño de los pines de cliente, usando
// los íconos clásicos de "gota" que trae Google.
// Cortes provisorios, a ajustar después con datos reales.
const RANGOS_KILOS = [
    { min: -Infinity, max: 500, color: 'ltblue', tamano: 42, label: '< 500 kg' },
    { min: 500, max: 2000, color: 'blue', tamano: 50, label: '500 - 2.000 kg' },
    { min: 2000, max: 5000, color: 'orange', tamano: 58, label: '2.000 - 5.000 kg' },
    { min: 5000, max: Infinity, color: 'red', tamano: 66, label: '> 5.000 kg' },
];

function obtenerRangoPin(kilos) {
    return RANGOS_KILOS.find(r => kilos >= r.min && kilos < r.max) || RANGOS_KILOS[0];
}

// Si dos o más direcciones caen en la coordenada EXACTA (mismo edificio,
// mismo punto geocodificado), Google Maps solo deja clickear la de más
// arriba en el z-order — el resto queda "tapado" y es imposible de
// seleccionar. Acá las separamos con un pequeño desplazamiento circular
// (unos metros) para que todas queden clickeables individualmente.
function separarPinesSuperpuestos(pines) {
    const grupos = new Map();
    pines.forEach(p => {
        const key = `${Number(p.latitud).toFixed(5)},${Number(p.longitud).toFixed(5)}`;
        if (!grupos.has(key)) grupos.set(key, []);
        grupos.get(key).push(p);
    });

    const resultado = [];
    grupos.forEach(grupo => {
        if (grupo.length === 1) {
            resultado.push(grupo[0]);
            return;
        }
        const offsetGrados = 0.00007; // aprox. 7-8 metros
        grupo.forEach((p, i) => {
            const angulo = (2 * Math.PI * i) / grupo.length;
            resultado.push({
                ...p,
                latitud: Number(p.latitud) + offsetGrados * Math.cos(angulo),
                longitud: Number(p.longitud) + offsetGrados * Math.sin(angulo),
            });
        });
    });
    return resultado;
}

const ICONO_SUCURSAL_URL = 'https://maps.google.com/mapfiles/kml/shapes/homegardenbusiness.png';

// Paleta para diferenciar hojas de ruta cuando se dibujan varias a la vez
// (todas las de una sucursal). Se cicla si hay más hojas que colores.
const PALETA_HOJAS_RUTA = ['#7c3aed', '#dc2626', '#059669', '#d97706', '#2563eb', '#db2777', '#0891b2', '#65a30d'];

const estilos = {
    contenedor: { position: 'relative', width: '100%' },

    filaTituloAyuda: {
        position: 'absolute', top: 53, left: 100, zIndex: 5,
        display: 'flex', alignItems: 'center', gap: 8,
    },

    tituloBoton: {
        background: '#fff',
        padding: '8px 14px', borderRadius: 6, boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
        fontSize: 14, fontWeight: 600, color: '#1a202c', maxWidth: 420,
        display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
        border: 'double', textAlign: 'left', background: '#73a7fa', color: 'white'
    },

    botonAyuda: {
        width: 30, height: 30, borderRadius: '50%', border: 'none',
        background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
        fontSize: 14, fontWeight: 700, color: '#2b6cb0', cursor: 'pointer',
        flexShrink: 0, color: 'red', background: '#34e5eb'
    },

    overlayAyuda: {
        position: 'fixed', inset: 0, zIndex: 30,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    },

    modalAyuda: {
        background: '#fff', borderRadius: 8, padding: 24,
        maxWidth: 520, width: '90%', maxHeight: '80vh', overflowY: 'auto',
        fontSize: 13, lineHeight: 1.5, color: '#2d3748',
    },

    modalAyudaHeader: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 12,
    },

    contadorDirecciones: { fontWeight: 400, fontSize: 12, color: '#4a5568', marginLeft: 8 },

    avisoSinGeo: {
        position: 'absolute', top: 148, left: 12, zIndex: 5, background: '#fff7ed',
        border: '1px solid #fdba74', padding: '6px 12px', borderRadius: 6,
        boxShadow: '0 1px 4px rgba(0,0,0,0.2)', fontSize: 12, color: '#9a3412',
        display: 'flex', alignItems: 'center', gap: 8,
    },

    linkDescargaCsv: { color: '#9a3412', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' },

    referenciaColores: {
        position: 'absolute', bottom: 24, left: 12, zIndex: 5, background: '#fff',
        padding: '10px 14px', borderRadius: 6, boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
        fontSize: 12, color: '#2d3748',
    },

    referenciaTitulo: { fontWeight: 700, marginBottom: 6 },
    referenciaItem: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 },

    overlayDrawer: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 20 },

    drawer: {
        position: 'fixed', top: ALTO_NAVBAR, right: 0, height: `calc(100vh - ${ALTO_NAVBAR}px)`, width: 320, background: '#fff',
        boxShadow: '-2px 0 8px rgba(0,0,0,0.2)', zIndex: 21, padding: 20,
        overflowY: 'auto', boxSizing: 'border-box',
    },

    drawerHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
    drawerTitulo: { fontSize: 16, fontWeight: 700, margin: 0 },
    botonCerrar: { border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', lineHeight: 1, color: '#4a5568' },
    botonLimpiar: {
        border: '1px solid #cbd5e0', background: '#fff', borderRadius: 4, padding: '4px 10px',
        fontSize: 12, cursor: 'pointer', color: '#4a5568', marginRight: 8,
    },

    filtro: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 },
    filtroFila: { display: 'flex', gap: 12 },
    filtroMitad: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16, flex: 1, minWidth: 0 },
    label: { fontSize: 12, fontWeight: 600, color: '#4a5568' },
    ayuda: { fontSize: 11, color: '#a0aec0', marginTop: 2 },
    select: {
        padding: '8px', border: '1px solid #cbd5e0', borderRadius: 4, fontSize: 13,
        background: '#fff', width: '100%', boxSizing: 'border-box',
    },
    selectMultiple: {
        padding: '8px', border: '1px solid #cbd5e0', borderRadius: 4, fontSize: 13,
        background: '#fff', width: '100%', boxSizing: 'border-box', minHeight: 110,
    },

    spinnerOverlay: {
        position: 'absolute', inset: 0, zIndex: 4, display: 'flex', alignItems: 'center',
        justifyContent: 'center', background: 'rgba(255, 255, 255, 0.6)',
    },
    spinner: {
        width: 42, height: 42, border: '4px solid #cbd5e0', borderTopColor: '#2b6cb0',
        borderRadius: '50%', animation: 'mapa-ventas-envasado-girar 0.8s linear infinite',
    },

    popup: { maxWidth: 280, fontSize: 13 },
    popupTitulo: { margin: '0 0 4px 0', fontSize: 14 },
    tag: { display: 'inline-block', background: '#e2e8f0', color: '#2d3748', padding: '1px 6px', borderRadius: 3, fontSize: 11 },
    subtitulo: { fontWeight: 600, marginBottom: 4, marginTop: 8 },
    botonDescargaPopup: {
        display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, marginBottom: 4,
        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
        color: '#2b6cb0', fontSize: 12, fontWeight: 600,
    },
    tablaDetalle: { width: '100%', borderCollapse: 'collapse', fontSize: 12, border: '1px solid #cbd5e0' },
    tdDetalle: { padding: '3px 6px', border: '1px solid #cbd5e0' },
    tdNumero: { padding: '3px 6px', border: '1px solid #cbd5e0', textAlign: 'right' },
    thDetalle: { padding: '3px 6px', textAlign: 'left', fontSize: 11, color: '#718096', border: '1px solid #cbd5e0', background: '#f7fafc' },
    thNumero: { padding: '3px 6px', textAlign: 'right', fontSize: 11, color: '#718096', border: '1px solid #cbd5e0', background: '#f7fafc' },
};

function IconoEmbudo() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
        </svg>
    );
}

function IconoDescarga() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2b6cb0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
    );
}

// Convex hull (envolvente convexa) por el algoritmo de Andrew (monotone
// chain). Recibe puntos {lat, lng} y devuelve el polígono más chico que
// los contiene a todos. No sigue calles, es geometría pura.
function calcularConvexHull(puntos) {
    if (puntos.length < 3) return puntos;

    const pts = puntos
        .map(p => [p.lat, p.lng])
        .sort((a, b) => a[0] - b[0] || a[1] - b[1]);

    const cruz = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

    const inferior = [];
    for (const p of pts) {
        while (inferior.length >= 2 && cruz(inferior[inferior.length - 2], inferior[inferior.length - 1], p) <= 0) {
            inferior.pop();
        }
        inferior.push(p);
    }

    const superior = [];
    for (let i = pts.length - 1; i >= 0; i--) {
        const p = pts[i];
        while (superior.length >= 2 && cruz(superior[superior.length - 2], superior[superior.length - 1], p) <= 0) {
            superior.pop();
        }
        superior.push(p);
    }

    superior.pop();
    inferior.pop();
    return inferior.concat(superior).map(([lat, lng]) => ({ lat, lng }));
}

function generarAnios() {
    const actual = new Date().getFullYear();
    const anios = [];
    for (let a = actual; a >= actual - 5; a--) anios.push(a);
    return anios;
}

function calcularRangoFechas(anio, mes) {
    const desde = `${anio}-${String(mes).padStart(2, '0')}-01 00:00:00`;
    const ultimoDia = new Date(anio, mes, 0).getDate();
    const hasta = `${anio}-${String(mes).padStart(2, '0')}-${String(ultimoDia).padStart(2, '0')} 23:59:59`;
    return { desde, hasta };
}

function TablaDetalle({ cargando, detalle }) {
    if (cargando) return <p>Cargando...</p>;
    if (!detalle) return null;
    return (
        <>
            {detalle.resumenGas && detalle.resumenGas.length > 0 && (
                <table style={estilos.tablaDetalle}>
                    <tbody>
                        {detalle.resumenGas.map(g => (
                            <tr key={g.tipo_gas}>
                                <td style={estilos.tdDetalle}><strong>{g.tipo_gas}</strong></td>
                                <td style={estilos.tdNumero}>{g.cantidad_vendida}</td>
                                <td style={estilos.tdNumero}>{g.kilos_vendidos} kg</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            <p style={estilos.subtitulo}>Desglose por producto</p>
            <table style={estilos.tablaDetalle}>
                <thead>
                    <tr>
                        <th style={estilos.thDetalle}>Producto</th>
                        <th style={estilos.thNumero}>Cant.</th>
                        <th style={estilos.thNumero}>Kilos</th>
                    </tr>
                </thead>
                <tbody>
                    {detalle.productos && detalle.productos.map(d => (
                        <tr key={d.idproducto}>
                            <td style={estilos.tdDetalle}>{d.tipo_envase}</td>
                            <td style={estilos.tdNumero}>{d.cantidad_vendida}</td>
                            <td style={estilos.tdNumero}>{d.kilos_vendidos} kg</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
}

export default function MapaVentasEnvasado() {
    // OJO: no usamos useJsApiLoader a propósito — ver el comentario en
    // useGoogleMapsManual, arriba del componente, para el motivo.
    const isLoaded = useGoogleMapsManual(GOOGLE_MAPS_KEY);

    const hoy = new Date();
    const [anio, setAnio] = useState(hoy.getFullYear());
    const [mes, setMes] = useState(hoy.getMonth() + 1);

    // sucursales ahora es un ARRAY (multi-select), el resto sigue siendo simple
    const [filtros, setFiltros] = useState({
        sucursales: [], producto: '', comercial: '', chofer: '', tipocliente: ''
    });

    const [combos, setCombos] = useState({
        sucursales: [], productos: [], comerciales: [], choferes: [], tiposCliente: []
    });

    const [pines, setPines] = useState([]);
    const [cargandoPines, setCargandoPines] = useState(false);
    const [drawerAbierto, setDrawerAbierto] = useState(false);
    const [ayudaAbierta, setAyudaAbierta] = useState(false);

    const [pinSeleccionado, setPinSeleccionado] = useState(null);
    const [detallePin, setDetallePin] = useState(null);
    const [cargandoDetalle, setCargandoDetalle] = useState(false);

    const [meta, setMeta] = useState({ facturas_con_geo: 0, facturas_sin_geo: 0 });

    const [sucursalesPines, setSucursalesPines] = useState([]);
    const [sucursalSeleccionada, setSucursalSeleccionada] = useState(null);
    const [detalleSucursal, setDetalleSucursal] = useState(null);
    const [cargandoDetalleSucursal, setCargandoDetalleSucursal] = useState(false);

    // --- Hojas de ruta (Camino A: polígono automático por hoja) ---
    const [sucursalesHojaRuta, setSucursalesHojaRuta] = useState([]);
    const [hojasRuta, setHojasRuta] = useState([]);
    const [filtroHojaRuta, setFiltroHojaRuta] = useState({ sucursal: '', hojas: [] });
    const [infoPoligonoHoja, setInfoPoligonoHoja] = useState(null); // {position, id_hoja, nombre_hoja, nombre_sucursal}
    const poligonosHojaRutaRef = useRef([]); // [{polygon, hoja}]

    const mapRef = useRef(null);
    // Estado (no solo ref) para cuándo el mapa de Google terminó de
    // inicializar de verdad — mapRef.current por sí solo no dispara
    // re-ejecuciones de efectos al cambiar, así que sin este estado
    // los pines/casitas pueden quedar sin dibujarse si el mapa tarda
    // en estar listo un instante más que isLoaded.
    const [mapListo, setMapListo] = useState(false);
    const markersRef = useRef([]);
    const sucursalMarkersRef = useRef([]);

    const { desde, hasta } = calcularRangoFechas(anio, mes);

    useEffect(() => {
        logEventos(
            "accedio al menú Mapas venta envasado Netizar",
            "mapas",
            "mapas venta netizar ",
            localStorage.getItem("user"));
    }, []);

    useEffect(() => {
        async function cargarCombos() {
            try {
                const [sucursales, productos, tiposCliente] = await Promise.all([
                    fetch(`${UrlApi}${ENDPOINT_BASE}/filtros/sucursales/${desde}/${hasta}`).then(r => r.json()),
                    fetch(`${UrlApi}${ENDPOINT_BASE}/filtros/productos/${desde}/${hasta}`).then(r => r.json()),
                    fetch(`${UrlApi}${ENDPOINT_BASE}/filtros/tipos-cliente/${desde}/${hasta}`).then(r => r.json()),
                ]);
                setCombos(prev => ({ ...prev, sucursales, productos, tiposCliente }));
            } catch (e) {
                console.error('Error cargando combos de filtro:', e);
            }
        }
        async function cargarMeta() {
            try {
                const res = await fetch(`${UrlApi}${ENDPOINT_BASE}/meta/${desde}/${hasta}`);
                const data = await res.json();
                setMeta(data);
            } catch (e) {
                console.error('Error cargando meta:', e);
            }
        }
        async function cargarSucursalesPines() {
            try {
                const res = await fetch(`${UrlApi}${ENDPOINT_BASE}/sucursales/mapa/${desde}/${hasta}`);
                const data = await res.json();
                setSucursalesPines(data);
            } catch (e) {
                console.error('Error cargando pines de sucursales:', e);
            }
        }

        cargarCombos();
        cargarMeta();
        cargarSucursalesPines();
        setFiltros({ sucursales: [], producto: '', comercial: '', chofer: '', tipocliente: '' });
        setPinSeleccionado(null);
        setDetallePin(null);
        setSucursalSeleccionada(null);
        setDetalleSucursal(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anio, mes]);

    // Comerciales y choferes dependen además de qué sucursal(es) estén
    // elegidas en el filtro principal — así el usuario no tiene que
    // buscar entre nombres de otras sucursales.
    useEffect(() => {
        async function cargarComercialesYChoferes() {
            try {
                const sucursalParam = filtros.sucursales.length > 0
                    ? `?sucursal=${filtros.sucursales.join(',')}`
                    : '';
                const [comerciales, choferes] = await Promise.all([
                    fetch(`${UrlApi}${ENDPOINT_BASE}/filtros/comerciales/${desde}/${hasta}${sucursalParam}`).then(r => r.json()),
                    fetch(`${UrlApi}${ENDPOINT_BASE}/filtros/choferes/${desde}/${hasta}${sucursalParam}`).then(r => r.json()),
                ]);
                setCombos(prev => ({ ...prev, comerciales, choferes }));
            } catch (e) {
                console.error('Error cargando comerciales/choferes:', e);
            }
        }
        cargarComercialesYChoferes();
        // Si cambia la sucursal, el comercial/chofer elegido antes puede
        // ya no ser válido para la nueva selección — se resetea.
        setFiltros(prev => ({ ...prev, comercial: '', chofer: '' }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anio, mes, filtros.sucursales]);

    useEffect(() => {
        async function cargarPines() {
            setCargandoPines(true);
            try {
                const params = new URLSearchParams();
                if (filtros.sucursales.length > 0) params.append('sucursal', filtros.sucursales.join(','));
                if (filtros.producto) params.append('producto', filtros.producto);
                if (filtros.comercial) params.append('comercial', filtros.comercial);
                if (filtros.chofer) params.append('chofer', filtros.chofer);
                if (filtros.tipocliente) params.append('tipocliente', filtros.tipocliente);

                const query = params.toString() ? `?${params.toString()}` : '';
                const res = await fetch(`${UrlApi}${ENDPOINT_BASE}/mapa/${desde}/${hasta}${query}`);
                const data = await res.json();
                setPines(data);
            } catch (e) {
                console.error('Error cargando pines del mapa:', e);
                setPines([]);
            } finally {
                setCargandoPines(false);
            }
        }
        cargarPines();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anio, mes, filtros]);

    // ------------------------------------------------------------
    // Markers de cliente SIN clustering. Se saca @googlemaps/markerclusterer
    // por completo: se comportaba de forma inconsistente (agrupaba pines
    // aislados sin motivo real) y era imposible de depurar de forma remota.
    // Con el filtro de sucursal multi-select, el usuario controla el
    // volumen de pines cargados a la vez.
    // ------------------------------------------------------------
    useEffect(() => {
        if (!mapRef.current || !isLoaded) return;

        markersRef.current.forEach(m => m.setMap(null));
        markersRef.current = [];

        const pinesSeparados = separarPinesSuperpuestos(pines);

        const nuevosMarkers = pinesSeparados.map(pin => {
            const rango = obtenerRangoPin(Number(pin.kilos_total));
            const marker = new window.google.maps.Marker({
                position: { lat: Number(pin.latitud), lng: Number(pin.longitud) },
                title: `${pin.razon_social} — ${pin.kilos_total} kg`,
                map: mapRef.current,
                icon: {
                    url: `https://maps.google.com/mapfiles/ms/icons/${rango.color}-dot.png`,
                    scaledSize: new window.google.maps.Size(rango.tamano, rango.tamano),
                },
            });
            marker.addListener('click', () => {
                setPinSeleccionado(pin);
                cargarDetalle(pin.id_direccion);
            });
            return marker;
        });
        markersRef.current = nuevosMarkers;

        return () => {
            nuevosMarkers.forEach(m => m.setMap(null));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pines, isLoaded, mapListo]);

    useEffect(() => {
        if (!mapRef.current || !isLoaded) return;

        sucursalMarkersRef.current.forEach(m => m.setMap(null));
        sucursalMarkersRef.current = [];

        const nuevosMarkers = sucursalesPines.map(suc => {
            const marker = new window.google.maps.Marker({
                position: { lat: Number(suc.latitud), lng: Number(suc.longitud) },
                title: `Sucursal ${suc.nombre}`,
                map: mapRef.current,
                icon: {
                    url: ICONO_SUCURSAL_URL,
                    scaledSize: new window.google.maps.Size(36, 36),
                },
                zIndex: 2000,
            });
            marker.__sucursalData = suc;
            marker.addListener('click', () => {
                setSucursalSeleccionada(suc);
                cargarDetalleSucursal(suc.id_sucursal);
            });
            return marker;
        });
        sucursalMarkersRef.current = nuevosMarkers;

        return () => {
            nuevosMarkers.forEach(m => m.setMap(null));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sucursalesPines, isLoaded, mapListo]);

    // --- Hojas de ruta: cargar combo de sucursales una sola vez ---
    useEffect(() => {
        async function cargarSucursalesHojaRuta() {
            try {
                const res = await fetch(`${UrlApi}${ENDPOINT_BASE}/hojas-ruta/sucursales`);
                const data = await res.json();
                setSucursalesHojaRuta(data);
            } catch (e) {
                console.error('Error cargando sucursales de hojas de ruta:', e);
            }
        }
        cargarSucursalesHojaRuta();
    }, []);

    // --- Hojas de ruta: cargar combo de hojas cuando cambia la sucursal elegida ---
    useEffect(() => {
        async function cargarHojasRuta() {
            try {
                const idSucursal = filtroHojaRuta.sucursal || 'todas';
                const res = await fetch(`${UrlApi}${ENDPOINT_BASE}/hojas-ruta/${idSucursal}`);
                const data = await res.json();
                setHojasRuta(data);
            } catch (e) {
                console.error('Error cargando hojas de ruta:', e);
            }
        }
        cargarHojasRuta();
        setFiltroHojaRuta(prev => ({ ...prev, hojas: [] }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtroHojaRuta.sucursal]);

    // --- Hojas de ruta: dibujar polígono(s) (convex hull) ---
    // Si hay una hoja puntual elegida, dibuja solo esa. Si solo hay
    // sucursal elegida (sin hoja), dibuja TODAS las hojas de esa
    // sucursal a la vez, cada una con un color distinto.
    useEffect(() => {
        if (!mapRef.current || !isLoaded) return;

        poligonosHojaRutaRef.current.forEach(({ polygon }) => polygon.setMap(null));
        poligonosHojaRutaRef.current = [];
        setInfoPoligonoHoja(null);

        const idsHojasADibujar = filtroHojaRuta.hojas;

        if (idsHojasADibujar.length === 0) return;

        async function dibujarPoligonoDeHoja(idHoja, color) {
            try {
                const res = await fetch(`${UrlApi}${ENDPOINT_BASE}/hojas-ruta/direcciones/${idHoja}`);
                const data = await res.json();
                const { hoja, direcciones } = data;
                if (!hoja || !direcciones || direcciones.length < 3) {
                    console.warn(`[hoja-ruta] "${hoja ? hoja.nombre_hoja : idHoja}" sin suficientes direcciones (mínimo 3).`);
                    return null;
                }

                const puntos = direcciones.map(d => ({ lat: Number(d.latitud), lng: Number(d.longitud) }));
                const hull = calcularConvexHull(puntos);

                const poligono = new window.google.maps.Polygon({
                    paths: hull,
                    strokeColor: color,
                    strokeOpacity: 0.9,
                    strokeWeight: 2,
                    fillColor: color,
                    fillOpacity: 0.15,
                    map: mapRef.current,
                });

                poligono.addListener('click', (e) => {
                    setInfoPoligonoHoja({
                        position: { lat: e.latLng.lat(), lng: e.latLng.lng() },
                        id_hoja: hoja.id_hoja,
                        nombre_hoja: hoja.nombre_hoja,
                        nombre_sucursal: hoja.nombre_sucursal,
                    });
                });

                return { polygon: poligono, hull };
            } catch (e) {
                console.error('Error dibujando polígono de hoja de ruta:', e);
                return null;
            }
        }

        async function dibujarTodo() {
            const resultados = await Promise.all(
                idsHojasADibujar.map((idHoja, i) => dibujarPoligonoDeHoja(idHoja, PALETA_HOJAS_RUTA[i % PALETA_HOJAS_RUTA.length]))
            );
            const validos = resultados.filter(Boolean);
            poligonosHojaRutaRef.current = validos.map(v => ({ polygon: v.polygon }));

            if (validos.length > 0) {
                const bounds = new window.google.maps.LatLngBounds();
                validos.forEach(v => v.hull.forEach(p => bounds.extend(p)));
                mapRef.current.fitBounds(bounds);
            }
        }
        dibujarTodo();

        return () => {
            poligonosHojaRutaRef.current.forEach(({ polygon }) => polygon.setMap(null));
            poligonosHojaRutaRef.current = [];
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtroHojaRuta.hojas, isLoaded, mapListo]);

    const cargarDetalle = useCallback(async (idDireccion) => {
        setCargandoDetalle(true);
        setDetallePin(null);
        try {
            const res = await fetch(`${UrlApi}${ENDPOINT_BASE}/detalle/${idDireccion}/${desde}/${hasta}`);
            const data = await res.json();
            setDetallePin(data);
        } catch (e) {
            console.error('Error cargando detalle del pin:', e);
        } finally {
            setCargandoDetalle(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [desde, hasta]);

    const cargarDetalleSucursal = useCallback(async (idSucursal) => {
        setCargandoDetalleSucursal(true);
        setDetalleSucursal(null);
        try {
            const res = await fetch(`${UrlApi}${ENDPOINT_BASE}/sucursales/detalle/${idSucursal}/${desde}/${hasta}`);
            const data = await res.json();
            setDetalleSucursal(data);
        } catch (e) {
            console.error('Error cargando detalle de sucursal:', e);
        } finally {
            setCargandoDetalleSucursal(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [desde, hasta]);

    const onMapLoad = useCallback((map) => { mapRef.current = map; setMapListo(true); }, []);

    const handleFiltroChange = (campo) => (e) => {
        setFiltros(prev => ({ ...prev, [campo]: e.target.value }));
    };

    const limpiarFiltros = () => {
        setFiltros({ sucursales: [], producto: '', comercial: '', chofer: '', tipocliente: '' });
        setFiltroHojaRuta({ sucursal: '', hojas: [] });
        setPinSeleccionado(null);
        setDetallePin(null);
        setSucursalSeleccionada(null);
        setDetalleSucursal(null);
        setInfoPoligonoHoja(null);
    };

    // Multi-select nativo: junta los values de las <option> seleccionadas.
    const handleSucursalesChange = (e) => {
        const seleccionadas = Array.from(e.target.selectedOptions).map(o => o.value);
        setFiltros(prev => ({ ...prev, sucursales: seleccionadas }));
    };

    const tituloFiltros = useMemo(() => {
        const nombreMes = MESES.find(m => m.valor === mes)?.nombre || '';
        const partes = [`${nombreMes} ${anio}`];

        if (filtros.sucursales.length > 0) {
            const nombres = filtros.sucursales
                .map(id => combos.sucursales.find(s => String(s.id) === String(id))?.nombre)
                .filter(Boolean);
            if (nombres.length > 0) partes.push(nombres.join(', '));
        }
        if (filtros.producto) {
            const p = combos.productos.find(x => String(x.idproducto) === String(filtros.producto));
            if (p) partes.push(p.nombre);
        }
        if (filtros.comercial) {
            const c = combos.comerciales.find(x => String(x.id) === String(filtros.comercial));
            if (c) partes.push(c.nombre_apellido);
        }
        if (filtros.chofer) {
            const ch = combos.choferes.find(x => String(x.id) === String(filtros.chofer));
            if (ch) partes.push(ch.nombre_apellido);
        }
        if (filtros.tipocliente) {
            const t = combos.tiposCliente.find(x => String(x.id) === String(filtros.tipocliente));
            if (t) partes.push(t.descripcion);
        }
        return partes.join(' · ');
    }, [mes, anio, filtros, combos]);

    const descargarCsvSinGeo = () => {
        window.location.href = `${UrlApi}${ENDPOINT_BASE}/sin-geo/${desde}/${hasta}`;
    };

    const descargarFacturasDireccion = (idDireccion) => {
        window.location.href = `${UrlApi}${ENDPOINT_BASE}/facturas-direccion/${idDireccion}/${desde}/${hasta}`;
    };

    const descargarListaHojaRuta = (idHoja) => {
        window.location.href = `${UrlApi}${ENDPOINT_BASE}/hojas-ruta/descargar/${idHoja}`;
    };

    return (
        <div style={estilos.contenedor}>
            <style>{`
        @keyframes mapa-ventas-envasado-girar {
          to { transform: rotate(360deg); }
        }
      `}</style>
            <Navbar />

            <div style={estilos.filaTituloAyuda}>
                <button style={estilos.tituloBoton} onClick={() => setDrawerAbierto(true)} title="Cambiar filtros">
                    <IconoEmbudo />
                    <span>
                        {tituloFiltros}
                        <span style={estilos.contadorDirecciones}>{pines.length} direcciones</span>
                    </span>
                </button>
                <button style={estilos.botonAyuda} onClick={() => setAyudaAbierta(true)} title="¿Cómo funcionan los filtros?">
                    ?
                </button>
            </div>

            {ayudaAbierta && (
                <div style={estilos.overlayAyuda} onClick={() => setAyudaAbierta(false)}>
                    <div style={estilos.modalAyuda} onClick={(e) => e.stopPropagation()}>
                        <div style={estilos.modalAyudaHeader}>
                            <h3 style={{ margin: 0, fontSize: 16 }}>¿Cómo funciona el mapa?</h3>
                            <button style={estilos.botonCerrar} onClick={() => setAyudaAbierta(false)}>×</button>
                        </div>

                        <p>El mapa muestra las direcciones de entrega de clientes de envasado (garrafas), con un pin por cada dirección que tuvo ventas en el período elegido.</p>

                        <p><strong>Filtros disponibles</strong> (botón con el embudo, arriba a la izquierda):</p>
                        <ul>
                            <li><strong>Mes / Año:</strong> define el período de ventas que se muestra. Es el único filtro obligatorio — todo lo demás es opcional.</li>
                            <li><strong>Sucursal:</strong> podés elegir una o varias. Si no elegís ninguna, se muestran todas.</li>
                            <li><strong>Tipo de envase:</strong> filtra por producto puntual (ej. garrafa de 10kg).</li>
                            <li><strong>Comercial / Chofer:</strong> se actualizan solos según la sucursal que elijas, para no tener que buscar entre nombres de otras sucursales.</li>
                            <li><strong>Tipo de cliente:</strong> filtra por la categoría comercial del cliente.</li>
                        </ul>

                        <p><strong>Colores y tamaños de los pines:</strong> representan los kilos totales entregados en esa dirección durante el período — cuanto más grande y más "caliente" el color (celeste → azul → naranja → rojo), más kilos. La referencia de colores está abajo a la izquierda del mapa.</p>

                        <p><strong>Al clickear un pin:</strong> se abre la info del cliente con el total de envases/kilos, el desglose por producto (Butano/Propano y por tipo de envase), y un botón para descargar el detalle factura por factura de esa dirección en el período.</p>

                        <p><strong>Ícono de casa:</strong> marca la ubicación de una sucursal. Al clickear muestra el total de esa sucursal completa.</p>

                        <p><strong>Zona de hoja de ruta:</strong> en la parte de abajo del panel de filtros, podés elegir una o varias hojas de ruta para ver el área aproximada que cubren (dibujada como un polígono en el mapa). Al clickear el polígono podés descargar el listado de clientes de esa ruta.</p>

                        <p><strong>Aviso naranja</strong> (si aparece): indica que hay facturas del período que no se pudieron ubicar en el mapa por falta de dirección geolocalizada, con un link para descargar el detalle.</p>
                    </div>
                </div>
            )}

            {meta.facturas_sin_geo > 0 && (
                <div style={estilos.avisoSinGeo}>
                    <span>⚠ {meta.facturas_sin_geo} factura(s) no se muestran por falta de dirección</span>
                    <span style={estilos.linkDescargaCsv} onClick={descargarCsvSinGeo}>Descargar CSV</span>
                </div>
            )}

            <div style={estilos.referenciaColores}>
                <div style={estilos.referenciaTitulo}>Kilos por dirección</div>
                {RANGOS_KILOS.map(r => (
                    <div key={r.label} style={estilos.referenciaItem}>
                        <img src={`https://maps.google.com/mapfiles/ms/icons/${r.color}-dot.png`} alt="" style={{ width: 16, height: 16 }} />
                        <span>{r.label}</span>
                    </div>
                ))}
                <div style={estilos.referenciaItem}>
                    <img src={ICONO_SUCURSAL_URL} alt="" style={{ width: 16, height: 16 }} />
                    <span>Sucursal</span>
                </div>
            </div>

            {drawerAbierto && (
                <>
                    <div style={estilos.overlayDrawer} onClick={() => setDrawerAbierto(false)} />
                    <div style={estilos.drawer}>
                        <div style={estilos.drawerHeader}>
                            <h3 style={estilos.drawerTitulo}>Filtros</h3>
                            <div>
                                <button style={estilos.botonLimpiar} onClick={limpiarFiltros}>Limpiar filtros</button>
                                <button style={estilos.botonCerrar} onClick={() => setDrawerAbierto(false)}>×</button>
                            </div>
                        </div>

                        <div style={estilos.filtroFila}>
                            <div style={estilos.filtroMitad}>
                                <label style={estilos.label}>Mes</label>
                                <select style={estilos.select} value={mes} onChange={(e) => setMes(Number(e.target.value))}>
                                    {MESES.map(m => <option key={m.valor} value={m.valor}>{m.nombre}</option>)}
                                </select>
                            </div>
                            <div style={estilos.filtroMitad}>
                                <label style={estilos.label}>Año</label>
                                <select style={estilos.select} value={anio} onChange={(e) => setAnio(Number(e.target.value))}>
                                    {generarAnios().map(a => <option key={a} value={a}>{a}</option>)}
                                </select>
                            </div>
                        </div>

                        <div style={estilos.filtro}>
                            <label style={estilos.label}>Sucursal (podés elegir varias)</label>
                            <select
                                style={estilos.selectMultiple}
                                multiple
                                value={filtros.sucursales}
                                onChange={handleSucursalesChange}
                            >
                                {combos.sucursales.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                            </select>
                            <span style={estilos.ayuda}>Ctrl/Cmd + click para elegir más de una. Vacío = todas.</span>
                        </div>

                        <div style={estilos.filtroFila}>
                            <div style={estilos.filtroMitad}>
                                <label style={estilos.label}>Tipo de envase</label>
                                <select style={estilos.select} value={filtros.producto} onChange={handleFiltroChange('producto')}>
                                    <option value="">Todos</option>
                                    {combos.productos.map(p => <option key={p.idproducto} value={p.idproducto}>{p.nombre}</option>)}
                                </select>
                            </div>
                            <div style={estilos.filtroMitad}>
                                <label style={estilos.label}>Tipo de cliente</label>
                                <select style={estilos.select} value={filtros.tipocliente} onChange={handleFiltroChange('tipocliente')}>
                                    <option value="">Todos</option>
                                    {combos.tiposCliente.map(t => <option key={t.id} value={t.id}>{t.descripcion}</option>)}
                                </select>
                            </div>
                        </div>

                        <div style={estilos.filtroFila}>
                            <div style={estilos.filtroMitad}>
                                <label style={estilos.label}>Comercial</label>
                                <select style={estilos.select} value={filtros.comercial} onChange={handleFiltroChange('comercial')}>
                                    <option value="">Todos</option>
                                    {combos.comerciales.map(c => <option key={c.id} value={c.id}>{c.nombre_apellido}</option>)}
                                </select>
                            </div>
                            <div style={estilos.filtroMitad}>
                                <label style={estilos.label}>Chofer</label>
                                <select style={estilos.select} value={filtros.chofer} onChange={handleFiltroChange('chofer')}>
                                    <option value="">Todos</option>
                                    {combos.choferes.map(c => <option key={c.id} value={c.id}>{c.nombre_apellido}</option>)}
                                </select>
                            </div>
                        </div>

                        <hr style={{ margin: '20px 0', border: 'none', borderTop: '1px solid #e2e8f0' }} />
                        <h4 style={{ fontSize: 14, margin: '0 0 12px 0' }}>Zona de hoja de ruta</h4>

                        <div style={estilos.filtro}>
                            <label style={estilos.label}>Sucursal</label>
                            <select
                                style={estilos.select}
                                value={filtroHojaRuta.sucursal}
                                onChange={(e) => setFiltroHojaRuta(prev => ({ ...prev, sucursal: e.target.value }))}
                            >
                                <option value="">Todas</option>
                                {sucursalesHojaRuta.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                            </select>
                        </div>

                        <div style={estilos.filtro}>
                            <label style={estilos.label}>Hoja de ruta (podés elegir varias)</label>
                            <select
                                style={estilos.selectMultiple}
                                multiple
                                value={filtroHojaRuta.hojas}
                                onChange={(e) => {
                                    const seleccionadas = Array.from(e.target.selectedOptions).map(o => o.value);
                                    setFiltroHojaRuta(prev => ({ ...prev, hojas: seleccionadas }));
                                }}
                            >
                                {hojasRuta.map(h => <option key={h.id} value={h.id}>{h.nombre}</option>)}
                            </select>
                            <span style={estilos.ayuda}>Ctrl/Cmd + click para elegir más de una. Ninguna elegida = zona oculta.</span>
                        </div>
                    </div>
                </>
            )}

            {(!isLoaded || cargandoPines) && (
                <div style={estilos.spinnerOverlay}>
                    <div style={estilos.spinner} />
                </div>
            )}

            {isLoaded && (
                <GoogleMap
                    mapContainerStyle={MAP_CONTAINER_STYLE}
                    center={CENTRO_DEFAULT}
                    zoom={7}
                    onLoad={onMapLoad}
                    options={{
                        fullscreenControl: false,
                        mapTypeControlOptions: {
                            style: window.google.maps.MapTypeControlStyle.DROPDOWN_MENU,
                        },
                    }}
                >
                    {pinSeleccionado && (
                        <InfoWindow
                            position={{ lat: Number(pinSeleccionado.latitud), lng: Number(pinSeleccionado.longitud) }}
                            onCloseClick={() => { setPinSeleccionado(null); setDetallePin(null); }}
                        >
                            <div style={estilos.popup}>
                                <h4 style={estilos.popupTitulo}>{pinSeleccionado.razon_social}</h4>
                                <p>{pinSeleccionado.direccion_entrega}</p>
                                {pinSeleccionado.tipo_cliente && <p style={estilos.tag}>{pinSeleccionado.tipo_cliente}</p>}
                                {pinSeleccionado.telefono_1 && <p>Tel: {pinSeleccionado.telefono_1}</p>}
                                <hr />
                                <p><strong>{pinSeleccionado.cantidad_total_envases}</strong> envases · <strong>{pinSeleccionado.kilos_total}</strong> kg</p>
                                <button
                                    style={estilos.botonDescargaPopup}
                                    onClick={() => descargarFacturasDireccion(pinSeleccionado.id_direccion)}
                                    title="Descargar detalle de facturas de esta dirección"
                                >
                                    <IconoDescarga /> Descargar facturas (TXT)
                                </button>
                                <TablaDetalle cargando={cargandoDetalle} detalle={detallePin} />
                            </div>
                        </InfoWindow>
                    )}

                    {sucursalSeleccionada && (
                        <InfoWindow
                            position={{ lat: Number(sucursalSeleccionada.latitud), lng: Number(sucursalSeleccionada.longitud) }}
                            onCloseClick={() => { setSucursalSeleccionada(null); setDetalleSucursal(null); }}
                        >
                            <div style={estilos.popup}>
                                <h4 style={estilos.popupTitulo}>Sucursal {sucursalSeleccionada.nombre}</h4>
                                <p><strong>{sucursalSeleccionada.cantidad_total_envases}</strong> envases · <strong>{sucursalSeleccionada.kilos_total}</strong> kg (total sucursal)</p>
                                <TablaDetalle cargando={cargandoDetalleSucursal} detalle={detalleSucursal} />
                            </div>
                        </InfoWindow>
                    )}

                    {infoPoligonoHoja && (
                        <InfoWindow
                            position={infoPoligonoHoja.position}
                            onCloseClick={() => setInfoPoligonoHoja(null)}
                        >
                            <div style={estilos.popup}>
                                <h4 style={estilos.popupTitulo}>{infoPoligonoHoja.nombre_hoja}</h4>
                                <p>Sucursal: {infoPoligonoHoja.nombre_sucursal || '—'}</p>
                                <button
                                    style={estilos.botonDescargaPopup}
                                    onClick={() => descargarListaHojaRuta(infoPoligonoHoja.id_hoja)}
                                    title="Descargar listado de clientes de esta hoja de ruta"
                                >
                                    <IconoDescarga /> Descargar listado (TXT)
                                </button>
                            </div>
                        </InfoWindow>
                    )}
                </GoogleMap>
            )}
        </div>
    );
}